<template>
    <div class="tw:px-6 tw:py-6">
        <!-- Error state -->
        <div v-if="error" class="tw:mb-6 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:gap-3">
            <span class="tw:text-sm tw:text-red-700">{{ error }}</span>
            <Button variant="danger" @click="refresh">
                {{ $t('back.dashboard.retry') }}
            </Button>
        </div>

        <!-- Loading skeleton -->
        <template v-if="loading">
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-6">
                <div class="tw:h-64 tw:bg-gray-100 tw:animate-pulse tw:rounded"></div>
            </div>
            <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-4 tw:gap-6">
                <div v-for="i in 4" :key="i" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <div class="tw:h-48 tw:bg-gray-100 tw:animate-pulse tw:rounded"></div>
                </div>
            </div>
        </template>

        <!-- Data -->
        <template v-else-if="data">
            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.fans.section_title') }}</h3>
            <p class="tw:text-sm tw:text-gray-500 tw:mb-6">{{ $t('back.dashboard.fans.section_description') }}</p>

            <!-- Age distribution column chart -->
            <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white tw:mb-6">
                <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.fans.age_title') }}</h3>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.fans.age_desc') }}</p>
                <Chart
                    v-if="ageChartData.length > 1"
                    type="ColumnChart"
                    :data="ageChartData"
                    :options="ageChartOptions"
                    :height="300"
                />
                <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                    {{ $t('back.dashboard.no_data') }}
                </div>
            </div>

            <!-- Bottom row: buyer types donut + 3 geography tables -->
            <div class="tw:grid tw:grid-cols-1 lg:tw:grid-cols-4 tw:gap-6 tw:mb-6">
                <!-- Buyer types donut -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:mb-1">{{ $t('back.dashboard.fans.buyer_types_title') }}</h3>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.fans.buyer_types_desc') }}</p>
                    <Chart
                        v-if="buyerTypeDonutData.length > 1"
                        :type="buyerChartType"
                        :data="buyerTypeDonutData"
                        :options="donutChartOptions"
                        :height="260"
                    />
                    <div v-else class="tw:h-64 tw:flex tw:items-center tw:justify-center tw:text-gray-400 tw:text-sm">
                        {{ $t('back.dashboard.no_data') }}
                    </div>
                </div>

                <!-- Geography tables -->
                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTables.provincias?.length">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.fans.geo_provincias') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.fans.geo_label') }}</th><th class="tw:text-right">{{ $t('back.dashboard.fans.geo_total') }}</th></tr></thead>
                        <tbody>
                            <tr v-for="(row, i) in geographyTables.provincias" :key="row.label" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                <td class="tw:py-1.5">{{ row.label }}</td>
                                <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTables.localidades?.length">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.fans.geo_localidades') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.fans.geo_label') }}</th><th class="tw:text-right">{{ $t('back.dashboard.fans.geo_total') }}</th></tr></thead>
                        <tbody>
                            <tr v-for="(row, i) in geographyTables.localidades" :key="row.label" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                <td class="tw:py-1.5">{{ row.label }}</td>
                                <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white" v-if="geographyTables.codigos_postales?.length">
                    <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.fans.geo_postcodes') }}</h4>
                    <table class="tw:w-full tw:text-sm">
                        <thead><tr class="tw:text-left tw:text-gray-500 tw:text-xs"><th>#</th><th>{{ $t('back.dashboard.fans.geo_label') }}</th><th class="tw:text-right">{{ $t('back.dashboard.fans.geo_total') }}</th></tr></thead>
                        <tbody>
                            <tr v-for="(row, i) in geographyTables.codigos_postales" :key="row.label" class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5 tw:text-gray-400">{{ i + 1 }}</td>
                                <td class="tw:py-1.5">{{ row.label }}</td>
                                <td class="tw:py-1.5 tw:text-right tw:font-medium">{{ formatNumber(row.total) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Attendance breakdown -->
            <div v-if="attendanceBreakdown" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-5 tw:bg-white">
                <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1">
                    <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900">{{ $t('back.dashboard.fans.attendance_title') }}</h3>
                    <Badge v-if="isMultiSeason" variant="info" :label="$t('back.dashboard.comparison')" />
                </div>
                <p class="tw:text-sm tw:text-gray-500 tw:mb-4">{{ $t('back.dashboard.fans.attendance_desc') }}</p>

                <!-- Multi-season: comparison table -->
                <template v-if="isMultiSeason && attendanceComparison">
                    <table class="tw:w-full tw:text-sm">
                        <thead>
                            <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                                <th class="tw:pb-2">{{ $t('back.dashboard.fans.attendance_type') }}</th>
                                <th class="tw:pb-2 tw:text-right" v-for="row in attendanceComparison" :key="row.season">{{ row.season }}</th>
                                <th class="tw:pb-2 tw:text-right" v-if="attendanceDelta">{{ $t('back.dashboard.delta') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_abonados') }} %</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right">{{ formatPercent(row.abonadoPct) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(attendanceDelta.abonadoPct)">{{ formatDelta(attendanceDelta.abonadoPct, 'pp') }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_entradas') }} %</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right">{{ formatPercent(row.entradaPct) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(attendanceDelta.entradaPct)">{{ formatDelta(attendanceDelta.entradaPct, 'pp') }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_invitaciones') }} %</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right">{{ formatPercent(row.invitacionPct) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right" :class="deltaClass(attendanceDelta.invitacionPct)">{{ formatDelta(attendanceDelta.invitacionPct, 'pp') }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100 tw:border-dashed">
                                <td class="tw:py-1.5 tw:text-gray-400 tw:text-xs">{{ $t('back.dashboard.fans.attendance_abonados') }} Media</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right tw:text-gray-400">{{ formatPercent(row.mediaAbonado) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right tw:text-gray-400" :class="deltaClass(attendanceDelta.mediaAbonado)">{{ formatDelta(attendanceDelta.mediaAbonado, 'pp') }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5 tw:text-gray-400 tw:text-xs">{{ $t('back.dashboard.fans.attendance_entradas') }} Media</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right tw:text-gray-400">{{ formatPercent(row.mediaEntrada) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right tw:text-gray-400" :class="deltaClass(attendanceDelta.mediaEntrada)">{{ formatDelta(attendanceDelta.mediaEntrada, 'pp') }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5 tw:text-gray-400 tw:text-xs">{{ $t('back.dashboard.fans.attendance_invitaciones') }} Media</td>
                                <td v-for="row in attendanceComparison" :key="row.season" class="tw:py-1.5 tw:text-right tw:text-gray-400">{{ formatPercent(row.mediaInvitacion) }}</td>
                                <td v-if="attendanceDelta" class="tw:py-1.5 tw:text-right tw:text-gray-400" :class="deltaClass(attendanceDelta.mediaInvitacion)">{{ formatDelta(attendanceDelta.mediaInvitacion, 'pp') }}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <!-- Single-season: cards + detail table -->
                <template v-else>
                    <!-- 3 KPI cards -->
                    <div class="tw:grid tw:grid-cols-3 tw:gap-4 tw:mb-4 tw:max-w-2xl tw:mx-auto">
                        <StatsCard
                            :title="$t('back.dashboard.fans.attendance_abonados')"
                            :value="formatPercent(attendanceBreakdown.abonadoPct)"
                            variant="success"
                        />
                        <StatsCard
                            :title="$t('back.dashboard.fans.attendance_entradas')"
                            :value="formatPercent(attendanceBreakdown.entradaPct)"
                            variant="primary"
                        />
                        <StatsCard
                            :title="$t('back.dashboard.fans.attendance_invitaciones')"
                            :value="formatPercent(attendanceBreakdown.invitacionPct)"
                            variant="warning"
                        />
                    </div>

                    <!-- Detail table -->
                    <table class="tw:w-full tw:text-sm">
                        <thead>
                            <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                                <th class="tw:pb-2">{{ $t('back.dashboard.fans.attendance_type') }}</th>
                                <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.fans.attendance_pct') }}</th>
                                <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.fans.attendance_avg') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_abonados') }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatPercent(attendanceBreakdown.abonadoPct) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(attendanceBreakdown.mediaAbonado) }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_entradas') }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatPercent(attendanceBreakdown.entradaPct) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(attendanceBreakdown.mediaEntrada) }}</td>
                            </tr>
                            <tr class="tw:border-t tw:border-gray-100">
                                <td class="tw:py-1.5">{{ $t('back.dashboard.fans.attendance_invitaciones') }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatPercent(attendanceBreakdown.invitacionPct) }}</td>
                                <td class="tw:py-1.5 tw:text-right">{{ formatNumber(attendanceBreakdown.mediaInvitacion) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </div>

            <!-- Multi-season breakdown -->
            <div v-if="isMultiSeason && seasonBreakdown?.length" class="tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:bg-white tw:mt-6">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-3">{{ $t('back.dashboard.economic.breakdown_title') }}</h4>
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:text-left tw:text-gray-500 tw:text-xs">
                            <th class="tw:pb-2">{{ $t('back.dashboard.season') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.fans.buyer_recurrentes') }}</th>
                            <th class="tw:pb-2 tw:text-right">{{ $t('back.dashboard.fans.buyer_nuevos') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in seasonBreakdown" :key="row.season" class="tw:border-t tw:border-gray-100">
                            <td class="tw:py-1.5">{{ row.season }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.recurrentes) }}</td>
                            <td class="tw:py-1.5 tw:text-right">{{ formatNumber(row.nuevos) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Empty state -->
        <div v-else class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-8 tw:text-center">
            <p class="tw:text-sm tw:text-gray-500">{{ $t('back.dashboard.no_data') }}</p>
        </div>
    </div>
</template>

<script setup>
    import { toRef } from 'vue';
    import { useFans } from '@/composables/admin/dashboard/useFans';
    import { formatPercent, formatNumber } from '@/utils/format';
    import Chart from '@/modules/admin/ui/Chart.vue';
    import StatsCard from '@/modules/admin/ui/StatsCard.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Badge from '@/modules/admin/ui/Badge.vue';

    const props = defineProps({
        seasonIds: { type: Array, required: true },
        seasons: { type: Array, required: true },
    });


    const seasonIdsRef = toRef(props, 'seasonIds');
    const seasonsRef = toRef(props, 'seasons');

    const {
        data,
        loading,
        error,
        isMultiSeason,
        ageChartData,
        buyerTypeDonutData,
        geographyTables,
        attendanceBreakdown,
        attendanceComparison,
        attendanceDelta,
        seasonBreakdown,
        ageChartOptions,
        donutChartOptions,
        buyerChartType,
        refresh,
    } = useFans(seasonIdsRef, seasonsRef);

    /** Clase de color según el valor del delta */
    const deltaClass = value => {
        if (value === 0 || value === null || value === undefined) return 'tw:text-gray-500';
        return value > 0
            ? 'tw:text-green-600 tw:font-semibold'
            : 'tw:text-red-600 tw:font-semibold';
    };

    /** Formatea el delta según su unidad */
    const formatDelta = (value, unit) => {
        if (value === null || value === undefined) return '-';
        const prefix = value >= 0 ? '+' : '';
        if (unit === 'pp') return `${prefix}${value.toFixed(1)} %`;
        return `${prefix}${value.toFixed(1)}`;
    };
</script>
